import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import { updateSuggestionStatus } from '../ui/utils/api';
import { applyToolCalls } from '../ui/utils/toolCalls/applyToolCalls';

import type { AcceptAllSuggestionsAction } from './types';

const nonActionableSuggestionKinds = new Set(['INFORMATIONAL', 'SUGGESTED_REMIX']);

export const acceptAllSuggestionsAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
) => AcceptAllSuggestionsAction =
	(api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined) => (): boolean => {
		const suggestionById = api?.aiSuggestions?.sharedState.currentState()?.suggestionById;
		if (!suggestionById || suggestionById.size === 0) {
			return false;
		}

		// Snapshot actionable suggestions up-front because dispatching the removal mutates plugin
		// state, so iterating the live Map during dispatch is unsafe. Informational suggestions are
		// dismissed, and remix suggestions require their dedicated generation flow.
		const suggestions = Array.from(suggestionById.values()).filter(
			({ actionKind }) => !actionKind || !nonActionableSuggestionKinds.has(actionKind),
		);
		if (suggestions.length === 0) {
			return false;
		}

		api?.core?.actions.execute(({ tr }) => {
			for (const suggestion of suggestions) {
				applyToolCalls({ tr, suggestion });
			}
			tr.setMeta('scrollIntoView', false);
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTIONS',
				ids: suggestions.map(({ id }) => id),
			});
			api?.showDiff?.commands.hideDiff?.({ tr });
			return tr;
		});

		const updates = suggestions.map(({ id }) => ({ id, status: 'ACTIONED' as const }));

		updateSuggestionStatus({ updates });

		return true;
	};
